// ─── FlowBuilderAddAgentModal ─────────────────────────────────────────────────
// Modal de configuração do nó Agente IA
// Fase 1: 3 abas (Identidade, Comportamento, Ferramentas) — 5 ferramentas
// Fase 3: +7 ferramentas avançadas + controles de segurança — 12 ferramentas total
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  IconButton,
  Divider,
  Slider,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  VolumeUp as AudioIcon,
  InsertDriveFile as DocIcon,
} from "@material-ui/icons";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { toast } from "react-toastify";
import api from "../../services/api";

// ─── Constantes ───────────────────────────────────────────────────────────────

const AGENT_MODELS = [
  { value: "gpt-4o",              label: "GPT-4o (OpenAI — Mais capaz)",           provider: "openai" },
  { value: "gpt-4o-mini",         label: "GPT-4o Mini (OpenAI — Rápido e barato)", provider: "openai" },
  { value: "gpt-4-turbo",         label: "GPT-4 Turbo (OpenAI)",                   provider: "openai" },
  { value: "gpt-4",               label: "GPT-4 (OpenAI)",                         provider: "openai" },
  { value: "gpt-3.5-turbo",       label: "GPT-3.5 Turbo (OpenAI)",                 provider: "openai" },
  { value: "gemini-1.5-flash",    label: "Gemini 1.5 Flash (Google — Rápido)",     provider: "gemini" },
  { value: "gemini-1.5-pro",      label: "Gemini 1.5 Pro (Google — Mais capaz)",   provider: "gemini" },
  { value: "gemini-2.0-flash",    label: "Gemini 2.0 Flash (Google — Rápido)",     provider: "gemini" },
];

// Ferramentas agrupadas por categoria
const TOOLS_GROUPS = [
  {
    category: "GESTÃO DE TICKET",
    color: "#1d4ed8",
    tools: [
      {
        key: "transferir_para_fila",
        label: "Transferir para fila",
        description: "Move o ticket para uma fila de atendimento pelo nome (ex: 'Vendas', 'Suporte')",
        emoji: "📋",
      },
      {
        key: "transferir_para_atendente",
        label: "Transferir para atendente",
        description: "Atribui o ticket a um atendente humano específico pelo nome",
        emoji: "👤",
      },
      {
        key: "encerrar_atendimento",
        label: "Encerrar atendimento",
        description: "Fecha o ticket quando o atendimento for concluído",
        emoji: "✅",
      },
    ],
  },
  {
    category: "ORGANIZAÇÃO",
    color: "#7c3aed",
    tools: [
      {
        key: "adicionar_tag",
        label: "Adicionar tag",
        description: "Aplica uma tag ao contato para classificação (ex: 'Interessado', 'VIP')",
        emoji: "🏷️",
      },
      {
        key: "remover_tag",
        label: "Remover tag",
        description: "Remove uma tag do contato",
        emoji: "🗑️",
      },
      {
        key: "mover_lane_kanban",
        label: "Mover lane no Kanban",
        description: "Move o ticket para uma coluna do Kanban (ex: 'Proposta Enviada', 'Fechado')",
        emoji: "🗂️",
      },
    ],
  },
  {
    category: "DADOS",
    color: "#0d9488",
    tools: [
      {
        key: "salvar_dado_cliente",
        label: "Salvar dado do cliente",
        description: "Guarda um campo personalizado do contato (ex: CPF, cidade, interesse)",
        emoji: "💾",
      },
      {
        key: "consultar_dado_cliente",
        label: "Consultar dado do cliente",
        description: "Lê um campo personalizado previamente salvo do contato",
        emoji: "🔍",
      },
    ],
  },
  {
    category: "VENDAS",
    color: "#b45309",
    tools: [
      {
        key: "marcar_como_venda",
        label: "Marcar como venda",
        description: "Registra uma venda no ticket com o valor monetário (ex: R$ 150,00)",
        emoji: "💰",
      },
      {
        key: "enviar_mensagem_template",
        label: "Enviar template",
        description: "Envia um template WhatsApp aprovado pela Meta (somente Cloud API)",
        emoji: "📨",
      },
    ],
  },
  {
    category: "AUTOMAÇÃO",
    color: "#0369a1",
    tools: [
      {
        key: "agendar_followup",
        label: "Agendar followup",
        description: "Agenda envio de mensagem futura para reativação, lembretes ou pós-venda",
        emoji: "⏰",
      },
    ],
  },
  {
    category: "INTEGRAÇÕES EXTERNAS",
    color: "#dc2626",
    tools: [
      {
        key: "fazer_requisicao_http",
        label: "Fazer requisição HTTP",
        description: "Consulta APIs externas via GET ou POST (requer domínios autorizados em Configurações > Integrações)",
        emoji: "🌐",
        warning: true,
      },
    ],
  },
  {
    category: "CONTEÚDO",
    color: "#0369a1",
    tools: [
      {
        key: "enviar_midia_salva",
        label: "Enviar mídia salva",
        description: "Envia imagens, áudios, vídeos ou documentos pré-cadastrados na aba Biblioteca de Mídias deste agente",
        emoji: "🎬",
      },
    ],
  },
];

// Lista plana para contagem e defaults
const ALL_TOOL_KEYS = TOOLS_GROUPS.flatMap(g => g.tools.map(t => t.key));

// ─── Estado inicial ───────────────────────────────────────────────────────────

const buildDefaultTools = () => {
  const tools = {};
  ALL_TOOL_KEYS.forEach(k => { tools[k] = false; });
  // Ferramentas habilitadas por padrão
  tools.transferir_para_fila   = true;
  tools.adicionar_tag           = true;
  tools.salvar_dado_cliente     = true;
  tools.encerrar_atendimento    = true;
  return tools;
};

const INITIAL_STATE = {
  name:             "Agente IA",
  systemPrompt:     "Você é um assistente de atendimento simpático e profissional. Responda as dúvidas do cliente de forma clara e objetiva. Colete informações relevantes e encaminhe para o setor correto quando necessário.",
  model:            "gpt-4o-mini",
  apiKey:           "",
  geminiApiKey:     "",
  temperature:      0.7,
  maxTokens:        1000,
  maxMessages:      10,
  maxTurns:         5,
  // Comportamento humanizado
  replyDelay:       "medium",
  // Segurança
  maxCostUsd:       1.0,
  forbiddenWords:   "",
  escalateToHuman:  false,
  // Ferramentas
  tools:            buildDefaultTools(),
};

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  btnWrapper:     { position: "relative" },
  buttonProgress: { color: green[500], position: "absolute", top: "50%", left: "50%", marginTop: -12, marginLeft: -12 },
  dialogTitle: {
    background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
    borderBottom: "1px solid #c4b5fd",
    paddingBottom: theme.spacing(1.5),
  },
  tabRoot: {
    minWidth: 0,
    flex: 1,
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "none",
  },
  tabsRoot: {
    backgroundColor: "#f5f3ff",
    borderRadius: "8px",
    marginBottom: theme.spacing(2),
  },
  tabIndicator: {
    backgroundColor: "#7c3aed",
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#6d28d9",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
  },
  categoryLabel: {
    fontSize: "0.68rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 4,
    marginTop: 12,
    paddingLeft: 2,
  },
  toolRow: {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px 10px",
    borderRadius: 8,
    marginBottom: 4,
    border: "1px solid #e9d5ff",
    background: "#faf5ff",
    gap: 6,
    transition: "background 0.15s",
    "&:hover": { background: "#f3e8ff" },
  },
  toolRowEnabled: {
    background: "#f3e8ff",
    borderColor: "#c4b5fd",
  },
  toolRowWarning: {
    borderColor: "#fcd34d",
    background: "#fffbeb",
    "&:hover": { background: "#fef3c7" },
  },
  toolRowWarningEnabled: {
    borderColor: "#f59e0b",
    background: "#fef3c7",
  },
  helperText: {
    fontSize: "0.72rem",
    color: "#7c3aed",
    marginTop: 2,
  },
  sliderRoot: {
    color: "#7c3aed",
  },
  securityBox: {
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 8,
    padding: "12px 14px",
    marginTop: 12,
  },
  warningBadge: {
    display: "inline-block",
    background: "#fef3c7",
    border: "1px solid #f59e0b",
    borderRadius: 4,
    padding: "1px 6px",
    fontSize: "0.65rem",
    fontWeight: 700,
    color: "#92400e",
    marginLeft: 4,
    verticalAlign: "middle",
  },
}));

// ─── Painel de aba ────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ minHeight: 340 }}>
      {value === index && <Box pt={1}>{children}</Box>}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const FlowBuilderAddAgentModal = ({ open, onSave, data, onUpdate, close, agents = [] }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [tab, setTab]                       = useState(0);
  const [showKey, setShowKey]               = useState(false);
  const [agent, setAgent]                   = useState(INITIAL_STATE);
  const [errors, setErrors]                 = useState({});
  const [labels, setLabels]                 = useState({ title: "Adicionar Agente IA", btn: "Adicionar" });
  // Toggle: "saved" = usa agente do banco | "inline" = config no nó
  const [mode, setMode]                     = useState("inline");
  const [selectedSavedId, setSelectedSavedId] = useState("");
  // ── Biblioteca de Mídias ──────────────────────────────────────────────────
  const [medias, setMedias]                 = useState([]);
  const [mediasLoading, setMediasLoading]   = useState(false);
  const [uploadOpen, setUploadOpen]         = useState(false);
  const [uploadFile, setUploadFile]         = useState(null);
  const [uploadLabel, setUploadLabel]       = useState("");
  const [uploadDesc, setUploadDesc]         = useState("");
  const [uploading, setUploading]           = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [agentDbId, setAgentDbId]           = useState(null);
  const fileInputRef                        = useRef(null);
  const autoSavedIdRef                      = useRef(null);

  // ── Carregar dados ao abrir ──────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    autoSavedIdRef.current = null;

    // Resolver ID do agente:
    // - Agents page (edit): data.id é número (ID do agente no banco)
    // - FlowBuilder (saved): data.data.agentId tem o ID
    // - FlowBuilder (inline) / criação: null → auto-save ao fazer upload
    const propId = typeof data?.id === "number"
      ? data.id
      : (data?.data?.agentId || null);
    setAgentDbId(propId);
    setMedias([]);

    if (open === "edit") {
      setLabels({ title: "Editar Agente IA", btn: "Salvar" });
      // data é o nó React Flow completo; agentId fica em data.data.agentId
      if (data?.data?.agentId) {
        setMode("saved");
        setSelectedSavedId(String(data.data.agentId));
      } else if (data?.data?.agentSettings) {
        setMode("inline");
        const saved = data.data.agentSettings;
        const mergedTools = { ...buildDefaultTools(), ...(saved.tools || {}) };
        setAgent({ ...INITIAL_STATE, ...saved, tools: mergedTools });
      }
    } else if (open === "create") {
      setLabels({ title: "Adicionar Agente IA", btn: "Adicionar" });
      setMode("inline");
      setSelectedSavedId("");
      setAgent(INITIAL_STATE);
      setErrors({});
      setTab(0);
    }
    return () => { isMounted.current = false; };
  }, [open, data]);

  // ── Carregar mídias ──────────────────────────────────────────────────────
  const fetchMedias = useCallback(async (overrideId = null) => {
    const id = overrideId ?? agentDbId;
    if (!id) return;
    setMediasLoading(true);
    try {
      const { data: list } = await api.get(`/agents/${id}/media`);
      if (isMounted.current) setMedias(list);
    } catch {
      // silencioso
    } finally {
      if (isMounted.current) setMediasLoading(false);
    }
  }, [agentDbId]);

  useEffect(() => {
    if (tab === 3) fetchMedias();
  }, [tab, agentDbId, fetchMedias]);

  // ── Auto-save do agente (quando ainda não tem ID no banco) ────────────────
  const handleAutoSave = useCallback(async () => {
    if (!agent.name?.trim()) {
      toast.error("Defina um nome pro agente antes de adicionar mídias");
      return null;
    }
    try {
      const { data: saved } = await api.post("/agents", {
        name: agent.name.trim(),
        config: agent,
      });
      if (isMounted.current) {
        setAgentDbId(saved.id);
        autoSavedIdRef.current = saved.id;
      }
      return saved.id;
    } catch {
      toast.error("Erro ao salvar agente automaticamente");
      return null;
    }
  }, [agent]);

  // ── Upload de mídia ───────────────────────────────────────────────────────

  const handleUploadSubmit = async () => {
    if (!uploadFile) { toast.error("Selecione um arquivo"); return; }
    if (!uploadLabel.trim()) { toast.error("Label obrigatório"); return; }

    let effectiveId = agentDbId;

    // Se ainda não tem ID, salvar o agente primeiro
    if (!effectiveId) {
      setUploading(true);
      effectiveId = await handleAutoSave();
      if (!effectiveId) { setUploading(false); return; }
    }

    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("label", uploadLabel.trim());
    fd.append("description", uploadDesc.trim());

    setUploading(true);
    setUploadProgress(0);
    try {
      await api.post(`/agents/${effectiveId}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: e => setUploadProgress(Math.round((e.loaded / e.total) * 100))
      });
      toast.success("Mídia adicionada!");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadLabel("");
      setUploadDesc("");
      fetchMedias(effectiveId);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao fazer upload");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await api.delete(`/agents/${agentDbId}/media/${mediaId}`);
      setMedias(prev => prev.filter(m => m.id !== mediaId));
      toast.success("Mídia removida");
    } catch {
      toast.error("Erro ao remover mídia");
    }
  };

  const mediaIcon = (type) => {
    if (type === "image") return <ImageIcon style={{ fontSize: 28, color: "#7c3aed" }} />;
    if (type === "video") return <VideoIcon style={{ fontSize: 28, color: "#0369a1" }} />;
    if (type === "audio") return <AudioIcon style={{ fontSize: 28, color: "#0d9488" }} />;
    return <DocIcon style={{ fontSize: 28, color: "#b45309" }} />;
  };

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  const set = (field, value) =>
    setAgent(prev => ({ ...prev, [field]: value }));

  const setTool = (key, value) =>
    setAgent(prev => ({ ...prev, tools: { ...prev.tools, [key]: value } }));

  const handleClose = () => {
    setTab(0);
    close(null);
  };

  // ── Validação ─────────────────────────────────────────────────────────────

  const isGeminiModel = agent.model?.startsWith("gemini-");

  const validate = () => {
    const e = {};
    if (!agent.name?.trim())                    e.name        = "Nome obrigatório";
    if (isGeminiModel) {
      if (!agent.geminiApiKey?.trim())          e.geminiApiKey = "Google API Key obrigatória";
    } else {
      if (!agent.apiKey?.trim())                e.apiKey      = "OpenAI API Key obrigatória";
    }
    if (!agent.model)                            e.model       = "Modelo obrigatório";
    if (!agent.systemPrompt?.trim())             e.systemPrompt = "Prompt obrigatório";
    if (agent.systemPrompt?.trim().length < 20) e.systemPrompt = "Mínimo 20 caracteres";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Salvar ────────────────────────────────────────────────────────────────

  const handleSave = () => {
    // ── Modo: agente salvo (referência por ID) ─────────────────────────────
    if (mode === "saved") {
      const savedAgent = agents.find(a => String(a.id) === String(selectedSavedId));
      if (!savedAgent) return; // Nenhum agente selecionado

      const savedToolCount = Object.values(savedAgent.config?.tools || {}).filter(Boolean).length;
      const savedPayload = {
        agentId:         savedAgent.id,
        agentName:       savedAgent.name,
        agentModel:      savedAgent.config?.model || "gpt-4o",
        agentToolCount:  savedToolCount,
        // Limpar config inline ao trocar para modo salvo
        agentSettings:   null,
      };

      if (open === "edit") {
        onUpdate({ ...data, data: savedPayload });
      } else {
        onSave(savedPayload);
      }
      handleClose();
      return;
    }

    // ── Modo: config inline ────────────────────────────────────────────────
    if (!validate()) {
      if (errors.name || errors.apiKey || errors.geminiApiKey || errors.model || errors.systemPrompt) setTab(0);
      return;
    }

    const payload = {
      name:            agent.name.trim(),
      systemPrompt:    agent.systemPrompt.trim(),
      model:           agent.model,
      apiKey:          agent.apiKey?.trim() || "",
      geminiApiKey:    agent.geminiApiKey?.trim() || "",
      temperature:     Number(agent.temperature),
      maxTokens:       Number(agent.maxTokens),
      maxMessages:     Number(agent.maxMessages),
      maxTurns:        Number(agent.maxTurns),
      maxCostUsd:      Number(agent.maxCostUsd) || 1.0,
      forbiddenWords:  String(agent.forbiddenWords || ""),
      escalateToHuman: Boolean(agent.escalateToHuman),
      replyDelay:      agent.replyDelay || "medium",
      tools:           agent.tools,
    };

    if (open === "edit") {
      onUpdate({ ...data, data: { agentSettings: payload, agentId: null, agentName: null, agentModel: null } });
    } else {
      onSave({ agentSettings: payload });
    }
    handleClose();
  };

  const toolCount = Object.values(agent.tools || {}).filter(Boolean).length;
  const totalTools = ALL_TOOL_KEYS.length;

  return (
    <Dialog
      open={open === "create" || open === "edit"}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      {/* ── Cabeçalho ── */}
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <SmartToyIcon style={{ color: "#7c3aed", fontSize: 22 }} />
          <Typography variant="h6" style={{ fontWeight: 700, color: "#4c1d95", fontSize: "1rem" }}>
            {labels.title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: "16px 20px" }}>

        {/* ── Toggle: Agente Salvo / Config Inline ────────────────────────── */}
        {agents.length > 0 && (
          <Box
            display="flex"
            style={{
              marginBottom: 14,
              background: "#f5f3ff",
              borderRadius: 10,
              padding: 3,
              gap: 3,
            }}
          >
            <button
              onClick={() => setMode("saved")}
              style={{
                flex: 1,
                padding: "7px 0",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.78rem",
                background: mode === "saved" ? "#7c3aed" : "transparent",
                color: mode === "saved" ? "white" : "#6d28d9",
                transition: "background 0.15s",
              }}
            >
              📌 Usar agente salvo
            </button>
            <button
              onClick={() => setMode("inline")}
              style={{
                flex: 1,
                padding: "7px 0",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.78rem",
                background: mode === "inline" ? "#7c3aed" : "transparent",
                color: mode === "inline" ? "white" : "#6d28d9",
                transition: "background 0.15s",
              }}
            >
              ⚙️ Configurar aqui
            </button>
          </Box>
        )}

        {/* ── Modo: Agente Salvo ────────────────────────────────────────────── */}
        {mode === "saved" && (
          <Box pt={1}>
            <Typography
              style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#6d28d9",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 6,
              }}
            >
              Selecionar agente
            </Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={selectedSavedId}
                onChange={e => setSelectedSavedId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em style={{ color: "#9ca3af" }}>Escolha um agente...</em>
                </MenuItem>
                {agents.map(a => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    🤖 {a.name}
                    <span style={{
                      marginLeft: 6,
                      fontSize: "0.7rem",
                      color: "#7c3aed",
                      background: "#ede9fe",
                      borderRadius: 4,
                      padding: "1px 5px",
                    }}>
                      {a.config?.model || "gpt-4o"}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Preview do agente selecionado */}
            {selectedSavedId && (() => {
              const sel = agents.find(a => String(a.id) === String(selectedSavedId));
              if (!sel) return null;
              const tools = sel.config?.tools || {};
              const tc = Object.values(tools).filter(Boolean).length;
              return (
                <Box
                  mt={2}
                  p={1.5}
                  style={{
                    background: "#f5f3ff",
                    border: "1px solid #e9d5ff",
                    borderRadius: 10,
                  }}
                >
                  <Typography style={{ fontSize: "0.8rem", fontWeight: 700, color: "#4c1d95", marginBottom: 4 }}>
                    {sel.name}
                  </Typography>
                  <Typography style={{ fontSize: "0.72rem", color: "#6d28d9", marginBottom: 6 }}>
                    {sel.config?.systemPrompt?.substring(0, 100) || "Sem prompt"}
                    {sel.config?.systemPrompt?.length > 100 ? "…" : ""}
                  </Typography>
                  <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                    <span style={{ background: "#ede9fe", color: "#4c1d95", borderRadius: 6, padding: "2px 7px", fontSize: "0.68rem", fontWeight: 700 }}>
                      {sel.config?.model || "gpt-4o"}
                    </span>
                    <span style={{ background: tc > 0 ? "#7c3aed" : "#9ca3af", color: "white", borderRadius: 6, padding: "2px 7px", fontSize: "0.68rem", fontWeight: 700 }}>
                      {tc}/12 tools
                    </span>
                    <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 6, padding: "2px 7px", fontSize: "0.68rem", fontWeight: 700 }}>
                      {sel.usageCount || 0} usos
                    </span>
                  </Box>
                  <Typography style={{ fontSize: "0.68rem", color: "#9ca3af", marginTop: 6 }}>
                    Edite o agente na página <strong>Agentes IA</strong> para atualizar em todos os fluxos.
                  </Typography>
                </Box>
              );
            })()}

            {!selectedSavedId && (
              <Typography style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: 8, textAlign: "center" }}>
                Selecione um agente salvo para usar neste nó
              </Typography>
            )}
          </Box>
        )}

        {/* ── Modo: Config Inline (abas) ────────────────────────────────────── */}
        {mode === "inline" && <>

        {/* ── Abas ── */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
          variant="fullWidth"
        >
          <Tab label="🪪 Identidade" classes={{ root: classes.tabRoot }} />
          <Tab label="⚙️ Comportamento" classes={{ root: classes.tabRoot }} />
          <Tab
            label={`🔧 Ferramentas (${toolCount}/${totalTools})`}
            classes={{ root: classes.tabRoot }}
          />
          <Tab
            label={`🎬 Mídias (${medias.length})`}
            classes={{ root: classes.tabRoot }}
          />
        </Tabs>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ABA 0 — IDENTIDADE                                                */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={0}>

          <Typography className={classes.sectionLabel}>Nome do agente</Typography>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Ex: Maria - Vendas Big Hair"
            value={agent.name}
            onChange={e => set("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />

          <Typography className={classes.sectionLabel}>Modelo</Typography>
          <FormControl variant="outlined" size="small" fullWidth error={!!errors.model}>
            <Select value={agent.model} onChange={e => set("model", e.target.value)}>
              {AGENT_MODELS.map(m => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
            {errors.model && (
              <Typography variant="caption" color="error">{errors.model}</Typography>
            )}
          </FormControl>

          {isGeminiModel ? (
            <>
              <Typography className={classes.sectionLabel}>Google API Key</Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="AIza..."
                type={showKey ? "text" : "password"}
                value={agent.geminiApiKey || ""}
                onChange={e => set("geminiApiKey", e.target.value)}
                error={!!errors.geminiApiKey}
                helperText={errors.geminiApiKey || "Chave da Google AI Studio (aistudio.google.com)"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowKey(v => !v)} edge="end">
                        {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <>
              <Typography className={classes.sectionLabel}>API Key OpenAI</Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="sk-..."
                type={showKey ? "text" : "password"}
                value={agent.apiKey}
                onChange={e => set("apiKey", e.target.value)}
                error={!!errors.apiKey}
                helperText={errors.apiKey || "Sua chave não é salva em nenhum outro lugar"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowKey(v => !v)} edge="end">
                        {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          <Typography className={classes.sectionLabel}>Prompt do sistema</Typography>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            rows={7}
            placeholder={
              "Você é [nome] da [empresa]. Seu papel é...\n\n" +
              "Regras:\n- Seja sempre educado\n- Colete o nome antes de avançar\n- Transfira para 'Suporte' se for reclamação técnica"
            }
            value={agent.systemPrompt}
            onChange={e => set("systemPrompt", e.target.value)}
            error={!!errors.systemPrompt}
            helperText={errors.systemPrompt || `${(agent.systemPrompt || "").length} chars — descreva a personalidade, regras e contexto da empresa`}
          />
        </TabPanel>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ABA 1 — COMPORTAMENTO + SEGURANÇA                                 */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={1}>

          {/* Temperature */}
          <Typography className={classes.sectionLabel}>
            Criatividade (temperature): <strong>{Number(agent.temperature).toFixed(1)}</strong>
          </Typography>
          <Box px={1}>
            <Slider
              classes={{ root: classes.sliderRoot }}
              min={0} max={1} step={0.1}
              value={Number(agent.temperature)}
              onChange={(_, v) => set("temperature", v)}
              marks={[
                { value: 0,   label: "0 — Preciso"  },
                { value: 0.5, label: "0.5"           },
                { value: 1,   label: "1 — Criativo"  },
              ]}
            />
          </Box>
          <Typography className={classes.helperText} style={{ marginBottom: 12 }}>
            Valores baixos tornam as respostas mais determinísticas; valores altos deixam a IA mais criativa.
          </Typography>

          <Divider style={{ margin: "12px 0" }} />

          {/* Max tokens */}
          <Typography className={classes.sectionLabel}>Máximo de tokens por resposta</Typography>
          <TextField
            variant="outlined" size="small" type="number" fullWidth
            inputProps={{ min: 50, max: 4000, step: 50 }}
            value={agent.maxTokens}
            onChange={e => set("maxTokens", Math.max(50, Math.min(4000, Number(e.target.value))))}
            helperText="50 – 4000. Padrão: 1000."
          />

          <Divider style={{ margin: "12px 0" }} />

          {/* Max messages */}
          <Typography className={classes.sectionLabel}>Memória — mensagens anteriores</Typography>
          <TextField
            variant="outlined" size="small" type="number" fullWidth
            inputProps={{ min: 1, max: 100, step: 1 }}
            value={agent.maxMessages}
            onChange={e => set("maxMessages", Math.max(1, Math.min(100, Number(e.target.value))))}
            helperText="Quantas mensagens anteriores do ticket serão enviadas como contexto. Padrão: 10."
          />

          <Divider style={{ margin: "12px 0" }} />

          {/* Max turns */}
          <Typography className={classes.sectionLabel}>Máximo de turns (anti-loop)</Typography>
          <TextField
            variant="outlined" size="small" type="number" fullWidth
            inputProps={{ min: 1, max: 20, step: 1 }}
            value={agent.maxTurns}
            onChange={e => set("maxTurns", Math.max(1, Math.min(20, Number(e.target.value))))}
            helperText="Quantas vezes a IA pode chamar ferramentas em sequência antes de responder. Padrão: 5."
          />

          {/* ── Velocidade de resposta ────────────────────────────────────── */}
          <Divider style={{ margin: "12px 0" }} />
          <Typography className={classes.sectionLabel}>Velocidade de resposta</Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={agent.replyDelay || "medium"}
              onChange={e => set("replyDelay", e.target.value)}
            >
              <MenuItem value="instant">⚡ Instantâneo — sem delay, sem digitando…</MenuItem>
              <MenuItem value="fast">🚀 Rápido — 1–8s (30 chars/s)</MenuItem>
              <MenuItem value="medium">💬 Médio — 2–20s (15 chars/s) — padrão</MenuItem>
              <MenuItem value="slow">🐢 Lento — 3–35s (8 chars/s)</MenuItem>
              <MenuItem value="very_slow">🐌 Muito lento — 5–60s (4 chars/s)</MenuItem>
            </Select>
          </FormControl>
          <Typography style={{ fontSize: "0.72rem", color: "#6b7280", marginTop: 4 }}>
            Simula digitação humana. Baileys: mostra "digitando…" no WhatsApp do cliente.
          </Typography>

          {/* ── Controles de segurança ────────────────────────────────────── */}
          <Divider style={{ margin: "16px 0 8px" }} />
          <Typography
            style={{
              fontSize: "0.7rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#16a34a",
              marginBottom: 8,
            }}
          >
            🛡️ Controles de Segurança
          </Typography>

          <div className={classes.securityBox}>

            {/* Max custo */}
            <Typography className={classes.sectionLabel} style={{ marginTop: 0 }}>
              Custo máximo por conversa (USD)
            </Typography>
            <TextField
              variant="outlined" size="small" type="number" fullWidth
              inputProps={{ min: 0.01, max: 50, step: 0.1 }}
              value={agent.maxCostUsd}
              onChange={e => set("maxCostUsd", Math.max(0.01, Number(e.target.value)))}
              helperText="Estimativa. Se exceder, o agente para e avisa o cliente. Padrão: $1.00"
            />

            {/* Palavras proibidas */}
            <Typography className={classes.sectionLabel}>
              Palavras proibidas
            </Typography>
            <TextField
              variant="outlined" size="small" fullWidth
              placeholder="concorrente, preço, não sei, ..."
              value={agent.forbiddenWords}
              onChange={e => set("forbiddenWords", e.target.value)}
              helperText="Separadas por vírgula. Se a mensagem contiver alguma, o agente não responde."
            />

            {/* Escalar para humano */}
            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(agent.escalateToHuman)}
                    onChange={e => set("escalateToHuman", e.target.checked)}
                    style={{ color: "#16a34a", padding: "2px 6px 0 0" }}
                  />
                }
                label={
                  <Typography style={{ fontSize: "0.83rem", fontWeight: 600, color: "#15803d" }}>
                    🙋 Escalar para humano quando solicitado
                  </Typography>
                }
              />
              <Typography style={{ fontSize: "0.72rem", color: "#6b7280", marginLeft: 26, marginTop: -2 }}>
                A IA instrui transferência para fila quando o cliente pedir para falar com atendente.
              </Typography>
            </Box>
          </div>
        </TabPanel>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ABA 2 — FERRAMENTAS (12 agrupadas)                                */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={2}>

          <Typography variant="body2" style={{ color: "#6d28d9", marginBottom: 6, fontSize: "0.82rem" }}>
            Habilite as ações que o agente pode executar automaticamente durante o atendimento.
          </Typography>

          {TOOLS_GROUPS.map(group => (
            <div key={group.category}>
              {/* Cabeçalho da categoria */}
              <Typography
                className={classes.categoryLabel}
                style={{ color: group.color }}
              >
                {group.category}
              </Typography>

              {group.tools.map(tool => {
                const enabled = !!agent.tools?.[tool.key];
                const isWarning = !!tool.warning;
                return (
                  <div
                    key={tool.key}
                    className={[
                      classes.toolRow,
                      enabled && !isWarning ? classes.toolRowEnabled : "",
                      isWarning && !enabled ? classes.toolRowWarning : "",
                      isWarning && enabled ? classes.toolRowWarningEnabled : "",
                    ].filter(Boolean).join(" ")}
                  >
                    <Checkbox
                      size="small"
                      checked={enabled}
                      onChange={e => setTool(tool.key, e.target.checked)}
                      style={{
                        color: isWarning ? "#f59e0b" : "#7c3aed",
                        padding: "2px 6px 0 0",
                        marginTop: 0,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <Typography style={{ fontSize: "0.83rem", fontWeight: 600, color: enabled ? (isWarning ? "#92400e" : "#4c1d95") : "#374151" }}>
                        {tool.emoji} {tool.label}
                        {isWarning && (
                          <span className={classes.warningBadge}>DOMÍNIOS AUTORIZADOS</span>
                        )}
                      </Typography>
                      <Typography style={{ fontSize: "0.73rem", color: enabled ? (isWarning ? "#b45309" : "#6d28d9") : "#9ca3af", lineHeight: 1.3 }}>
                        {tool.description}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Aviso se nenhuma ferramenta habilitada */}
          {toolCount === 0 && (
            <Box mt={1} p={1.5} style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, fontSize: "0.8rem", color: "#92400e" }}>
              ⚠️ Nenhuma ferramenta habilitada. O agente só poderá responder mensagens, sem executar ações.
            </Box>
          )}

          {/* Aviso específico para fazer_requisicao_http */}
          {agent.tools?.fazer_requisicao_http && (
            <Box mt={1} p={1.5} style={{ background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8, fontSize: "0.78rem", color: "#92400e" }}>
              ⚠️ <strong>Requisição HTTP ativa:</strong> A IA só poderá acessar domínios configurados em{" "}
              <strong>Configurações &gt; Integrações &gt; Domínios Autorizados</strong>.
              Sem domínios configurados, todas as requisições serão bloqueadas.
            </Box>
          )}
        </TabPanel>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ABA 3 — BIBLIOTECA DE MÍDIAS (apenas modo edição)                */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabPanel value={tab} index={3}>

            {/* Cabeçalho + botão upload */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography style={{ fontSize: "0.82rem", color: "#6d28d9", fontWeight: 600 }}>
                {medias.length}/20 mídias cadastradas
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                disabled={medias.length >= 20}
                onClick={() => setUploadOpen(true)}
                style={{ background: "#7c3aed", color: "white", fontSize: "0.75rem" }}
              >
                + Nova Mídia
              </Button>
            </Box>

            {mediasLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} style={{ color: "#7c3aed" }} />
              </Box>
            ) : medias.length === 0 ? (
              <Box
                p={3}
                style={{
                  background: "#f5f3ff",
                  border: "1px dashed #c4b5fd",
                  borderRadius: 10,
                  textAlign: "center",
                }}
              >
                <Typography style={{ fontSize: "0.82rem", color: "#9ca3af" }}>
                  Nenhuma mídia cadastrada.<br />
                  Clique em <strong>+ Nova Mídia</strong> para adicionar imagens, áudios, vídeos ou documentos.
                </Typography>
              </Box>
            ) : (
              <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {medias.map(m => (
                  <Box
                    key={m.id}
                    p={1.5}
                    style={{
                      background: "#faf5ff",
                      border: "1px solid #e9d5ff",
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {/* Ícone do tipo */}
                    <Box style={{ flexShrink: 0 }}>{mediaIcon(m.mediaType)}</Box>

                    {/* Informações */}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: "#4c1d95" }}>
                        {m.label}
                        <span style={{
                          marginLeft: 6,
                          background: "#ede9fe",
                          color: "#6d28d9",
                          borderRadius: 4,
                          padding: "1px 5px",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}>
                          {m.mediaType}
                        </span>
                      </Typography>
                      {m.description && (
                        <Typography style={{ fontSize: "0.72rem", color: "#6d28d9", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {m.description}
                        </Typography>
                      )}
                      <Typography style={{ fontSize: "0.66rem", color: "#9ca3af", marginTop: 1 }}>
                        {m.filename} · {formatBytes(m.fileSize)}
                      </Typography>
                    </Box>

                    {/* Botão deletar */}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMedia(m.id)}
                      style={{ color: "#ef4444" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Modal de upload */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle disableTypography>
                <Typography style={{ fontWeight: 700, color: "#4c1d95", fontSize: "0.95rem" }}>
                  🎬 Nova Mídia
                </Typography>
              </DialogTitle>
              <DialogContent dividers style={{ padding: "14px 18px" }}>

                {/* Área de upload */}
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${uploadFile ? "#7c3aed" : "#c4b5fd"}`,
                    borderRadius: 10,
                    padding: "18px 12px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: uploadFile ? "#f5f3ff" : "#faf5ff",
                    marginBottom: 12,
                  }}
                >
                  {uploadFile ? (
                    <>
                      <Typography style={{ fontSize: "0.82rem", fontWeight: 700, color: "#4c1d95" }}>
                        {uploadFile.name}
                      </Typography>
                      <Typography style={{ fontSize: "0.72rem", color: "#6d28d9" }}>
                        {formatBytes(uploadFile.size)} — clique para trocar
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon style={{ color: "#c4b5fd", fontSize: 32 }} />
                      <Typography style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: 4 }}>
                        Clique para selecionar<br />
                        <span style={{ fontSize: "0.68rem" }}>JPG, PNG, MP4, MP3, OGG, PDF, DOCX · máx 16 MB</span>
                      </Typography>
                    </>
                  )}
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/mpeg,audio/mpeg,audio/ogg,audio/wav,audio/aac,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 16 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 16 MB)"); return; }
                    setUploadFile(f);
                    if (!uploadLabel) {
                      setUploadLabel(f.name.replace(/\.[^.]+$/, "").replace(/[^a-z0-9]/gi, "_").toLowerCase().replace(/_+/g, "_").replace(/^_|_$/g, ""));
                    }
                  }}
                />

                <Typography style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6d28d9", marginBottom: 4 }}>
                  Label (identificador para a IA)
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="ex: audio_apresentacao, foto_produto"
                  value={uploadLabel}
                  onChange={e => setUploadLabel(e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""))}
                  style={{ marginBottom: 10 }}
                  helperText="snake_case, sem espaços. Será usado pela IA para identificar a mídia."
                />

                <Typography style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6d28d9", marginBottom: 4 }}>
                  Descrição (quando a IA deve usar)
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="ex: Áudio da Érica explicando o método em 2 minutos"
                  value={uploadDesc}
                  onChange={e => setUploadDesc(e.target.value)}
                  style={{ marginBottom: 4 }}
                />

                {uploading && (
                  <Box mt={1.5}>
                    <LinearProgress variant="determinate" value={uploadProgress} style={{ borderRadius: 4 }} />
                    <Typography style={{ fontSize: "0.68rem", color: "#6d28d9", textAlign: "center", marginTop: 2 }}>
                      Enviando… {uploadProgress}%
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions style={{ padding: "10px 16px", gap: 6 }}>
                <Button size="small" variant="outlined" onClick={() => { setUploadOpen(false); setUploadFile(null); setUploadLabel(""); setUploadDesc(""); }} style={{ borderColor: "#c4b5fd", color: "#7c3aed" }}>
                  Cancelar
                </Button>
                <Button size="small" variant="contained" onClick={handleUploadSubmit} disabled={uploading || !uploadFile || !uploadLabel.trim()} style={{ background: "#7c3aed", color: "white", fontWeight: 700 }}>
                  {uploading ? "Enviando…" : "Salvar"}
                </Button>
              </DialogActions>
            </Dialog>

            <Box mt={1.5} p={1} style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, fontSize: "0.72rem", color: "#15803d" }}>
              💡 A IA usará automaticamente a ferramenta <strong>enviar_midia_salva</strong> quando tiver mídias cadastradas.
              Ative o checkbox <strong>🎬 Enviar mídia salva</strong> na aba Ferramentas para controle manual.
            </Box>

        </TabPanel>

        </>} {/* Fim modo inline */}

      </DialogContent>

      {/* ── Ações ── */}
      <DialogActions style={{ padding: "12px 20px", gap: 8 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          style={{ borderColor: "#c4b5fd", color: "#7c3aed" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          style={{ background: "#7c3aed", color: "white", fontWeight: 700 }}
        >
          {labels.btn}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddAgentModal;
